import { createFileRoute, Link } from '@tanstack/react-router';
import { Header, Footer } from '@/components/marketing';
import { Button } from '@/components/ui/button';
import { products } from '@/lib/marketing-data';
import { cn } from '@/lib/utils';
import {
  ArrowRight,
  Search,
  Layers,
  FileText,
  Download,
  Code2,
  GitBranch,
  Shield,
  Check,
  Sparkles,
} from 'lucide-react';

export const Route = createFileRoute('/products/codespringforge')({
  component: CodeSpringForgePage,
});

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Search,
  Layers,
  FileText,
  Download,
};

// Fixed: All text colors updated for WCAG 2.2 contrast (4.5:1 minimum)
const tierColors: Record<string, { bg: string; text: string; label: string }> = {
  basic: { bg: 'bg-green-500/10', text: 'text-green-700', label: 'Free' },
  security: { bg: 'bg-yellow-500/10', text: 'text-yellow-700', label: 'Security' },
  pro: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Pro' },
  enterprise: { bg: 'bg-purple-500/10', text: 'text-purple-700', label: 'Enterprise' },
};

function CodeSpringForgePage() {
  const product = products.codespringforge;

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="pt-32 pb-16 sm:pt-40 sm:pb-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid gap-12 lg:grid-cols-2 lg:gap-24 items-center">
              <div>
                {/* Fixed: contrast 5.74:1 (was 4.48:1 with text-primary) */}
                <div className="inline-flex items-center gap-2 rounded-full border bg-blue-100 px-4 py-1.5 text-blue-700 mb-6">
                  <Code2 className="h-4 w-4" />
                  <span className="text-sm font-medium">{product.name}</span>
                </div>
                <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
                  {product.tagline}
                </h1>
                <p className="mt-6 text-xl text-muted-foreground">
                  {product.description}
                </p>
                <div className="mt-10 flex flex-col sm:flex-row gap-4">
                  <Link to="/register">
                    <Button size="lg" className="gap-2 w-full sm:w-auto">
                      Start Free <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/pricing">
                    <Button
                      size="lg"
                      variant="outline"
                      className="w-full sm:w-auto"
                    >
                      View Pricing
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Product Preview */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20 rounded-2xl blur-2xl" />
                <div className="relative rounded-xl border bg-card shadow-2xl overflow-hidden">
                  <div className="flex items-center gap-2 border-b bg-muted/50 px-4 py-3">
                    <div className="flex gap-1.5">
                      <div className="h-3 w-3 rounded-full bg-red-500" />
                      <div className="h-3 w-3 rounded-full bg-yellow-500" />
                      <div className="h-3 w-3 rounded-full bg-green-500" />
                    </div>
                    <span className="text-sm text-muted-foreground ml-2">
                      Analysis Results
                    </span>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="flex items-center gap-3">
                      <GitBranch className="h-5 w-5 text-primary" />
                      <span className="font-medium">Repository Analyzed</span>
                      <span className="ml-auto text-green-600 text-sm">Complete</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Layers className="h-5 w-5 text-primary" />
                      <span className="font-medium">47 Features Extracted</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Shield className="h-5 w-5 text-primary" />
                      <span className="font-medium">2 Security Issues Found</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-primary" />
                      <span className="font-medium">PRD Generated</span>
                      <span className="ml-auto text-muted-foreground text-sm">
                        12 sections
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 sm:py-24 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center mb-16">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                Powerful Capabilities
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Everything you need to understand and document your codebase.
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {product.features.map((feature) => {
                const Icon = iconMap[feature.icon] || Search;
                return (
                  <div
                    key={feature.title}
                    className="rounded-xl border bg-card p-6 hover:border-primary/50 transition-colors"
                  >
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="mt-4 font-semibold">{feature.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Capabilities Matrix */}
        <section className="py-16 sm:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center mb-16">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                Capabilities by Tier
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Choose the tier that matches your needs.
              </p>
            </div>

            <div className="mx-auto max-w-4xl">
              <div className="rounded-xl border bg-card overflow-hidden">
                <div className="grid grid-cols-2 gap-4 p-6">
                  {product.capabilities.map((capability) => {
                    const tierData = tierColors[capability.tier as keyof typeof tierColors] ?? {
                      bg: 'bg-muted',
                      text: 'text-muted-foreground',
                      label: capability.tier,
                    };
                    return (
                      <div
                        key={capability.name}
                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                      >
                        <div className="flex items-center gap-3">
                          <Check className="h-5 w-5 text-primary" />
                          <span className="text-sm font-medium">
                            {capability.name}
                          </span>
                        </div>
                        <span
                          className={cn(
                            'rounded-full px-2.5 py-0.5 text-xs font-medium',
                            tierData.bg,
                            tierData.text
                          )}
                        >
                          {tierData.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 sm:py-24 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center mb-16">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                How It Works
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Get from code to documentation in four simple steps.
              </p>
            </div>

            <div className="grid gap-8 lg:grid-cols-4">
              {[
                {
                  step: '01',
                  title: 'Connect Repository',
                  description:
                    'Link your GitHub, GitLab, or Bitbucket repository in seconds.',
                },
                {
                  step: '02',
                  title: 'AI Analysis',
                  description:
                    'Our AI scans your codebase, understanding architecture and patterns.',
                },
                {
                  step: '03',
                  title: 'Extract Features',
                  description:
                    'Features, capabilities, and technical debt are automatically identified.',
                },
                {
                  step: '04',
                  title: 'Generate PRD',
                  description:
                    'Get a comprehensive PRD with architecture insights and recommendations.',
                },
              ].map((item, index) => (
                <div key={item.step} className="relative">
                  {index < 3 && (
                    <div className="absolute top-12 left-[calc(50%+2rem)] hidden w-[calc(100%-4rem)] border-t-2 border-dashed border-border lg:block" />
                  )}
                  <div className="flex flex-col items-center text-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground text-2xl font-bold relative z-10">
                      {item.step}
                    </div>
                    <h3 className="mt-6 text-lg font-semibold">{item.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-2xl bg-primary p-8 sm:p-16 text-center">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-4 py-1.5 text-primary-foreground mb-6">
                <Sparkles className="h-4 w-4" />
                <span className="text-sm font-medium">Free to Start</span>
              </div>
              <h2 className="text-3xl font-bold text-primary-foreground sm:text-4xl">
                Ready to Analyze Your First Repository?
              </h2>
              <p className="mt-4 text-lg text-primary-foreground/80 max-w-2xl mx-auto">
                Sign up for free and analyze up to 3 repositories per month. No credit
                card required.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/register">
                  <Button
                    size="lg"
                    variant="secondary"
                    className="gap-2 bg-white text-primary hover:bg-white/90"
                  >
                    Start Free <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/contact" search={{ type: 'demo' }}>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-white/30 text-primary-foreground hover:bg-white/10"
                  >
                    See Demo
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
